/**
 * Formatting utilities for displaying currency and other values consistently
 */

/**
 * Format a number as currency ($X.XX)
 * 
 * @param amount The amount to format
 * @param locale The locale to use for formatting (default: 'en-US')
 * @param currency The currency to use (default: 'USD')
 * @returns Formatted currency string
 */
export function formatCurrency(
  amount: number | string,
  locale = 'en-US',
  currency = 'USD'
): string {
  // Convert string to number if needed
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle invalid values
  if (isNaN(numericAmount)) {
    return '$0.00';
  }
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericAmount);
}

/**
 * Format a date as a string (MM/DD/YYYY)
 * 
 * @param date The date to format
 * @returns Formatted date string
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) {
    return '';
  }
  
  // Convert string to Date if needed
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  
  // Handle invalid dates
  if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) {
    return '';
  }
  
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  }).format(dateObj);
}

/**
 * Format a phone number as (XXX) XXX-XXXX
 * 
 * @param phone The phone number to format
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if the input is valid
  if (cleaned.length !== 10) {
    return phone; // Return original if not 10 digits
  }
  
  // Format as (XXX) XXX-XXXX
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
}

/**
 * Clean and format phone number from database (removes +1 and formats properly)
 * 
 * @param phone The phone number from database
 * @returns Cleaned and formatted phone number or empty string
 */
export function cleanAndFormatPhone(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Remove all non-numeric characters including +1
  let cleaned = phone.replace(/\D/g, '');
  
  // If it starts with 1 and has 11 digits, remove the 1 (US country code)
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    cleaned = cleaned.slice(1);
  }
  
  // Only format if we have exactly 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return empty string if invalid
  return '';
}

/**
 * Format a percentage (X.XX%)
 * 
 * @param value The percentage value to format
 * @param decimalPlaces The number of decimal places to show (default: 2)
 * @returns Formatted percentage string
 */
export function formatPercentage(
  value: number | string,
  decimalPlaces = 2
): string {
  // Convert string to number if needed
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Handle invalid values
  if (isNaN(numericValue)) {
    return '0%';
  }
  
  return `${numericValue.toFixed(decimalPlaces)}%`;
} 